// src/pages/dashboard/Dashboard.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  useTheme,
  Divider,
  alpha,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import apiService, { Application } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Alkalmazások lekérdezése
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        // Itt a változás - a getApplications() már a teljes tömböt adja vissza
        const apps = await apiService.getApplications();
        setApplications(apps);
      } catch (err) {
        setError('Nem sikerült betölteni az alkalmazásokat');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    fetchApplications();
  }, []);

  // Alkalmazás indítása
  const handleStartApp = async (appId: string) => {
    try {
      await apiService.startApplication(appId);
      setApplications(prev =>
        prev.map(app => (app.id === appId ? { ...app, status: 'active' } : app))
      );
    } catch (err) {
      console.error('Hiba az alkalmazás indításakor:', err);
    }
  };

  // Alkalmazás leállítása
  const handleStopApp = async (appId: string) => {
    try {
      await apiService.stopApplication(appId);
      setApplications(prev =>
        prev.map(app => (app.id === appId ? { ...app, status: 'inactive' } : app))
      );
    } catch (err) {
      console.error('Hiba az alkalmazás leállításakor:', err);
    }
  };

  // Alkalmazás indítása új ablakban
  const handleOpenApp = (url: string) => {
    window.open(url, '_blank');
  };

  // Lehet-e kezelni az alkalmazást? Admin vagy tulajdonos jogosultság kell
  const canManageApp = user?.role === 'admin';

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Fejléc */}
        <Box>
          <Paper
            sx={{
              p: 3,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: 'white',
              mb: 3,
            }}
            elevation={4}
          >
            <Typography variant="h4" component="h1" gutterBottom>
              Üdvözöljük a TestPulse rendszerben!
            </Typography>
            <Typography variant="subtitle1">
              Ezen a felületen tudja kezelni és elindítani a tesztrendszereket.
            </Typography>
          </Paper>
        </Box>

        {/* Alkalmazás kártyák */}
        <Box>
          <Typography variant="h5" gutterBottom>
            Elérhető alkalmazások
          </Typography>
          <Divider sx={{ mb: 3 }} />

          {error && (
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
              }}
            >
              <Typography>{error}</Typography>
            </Paper>
          )}

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: {
              xs: '1fr',                     // Mobil: egy oszlop 
              sm: 'repeat(2, 1fr)',          // Tablet: két oszlop
              md: 'repeat(3, 1fr)'           // Asztali: három oszlop
            }, 
            gap: 3 
          }}>
            {applications.length === 0 ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography align="center">Nincsenek elérhető alkalmazások</Typography>
                </Paper>
              </Box>
            ) : (
              applications.map((app) => (
                <Card
                  key={app.id}
                  elevation={3}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'transform 0.3s, box-shadow 0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" component="h2">
                        {app.name}
                      </Typography>
                      <Chip
                        label={app.status === 'active' ? 'Aktív' : 'Inaktív'}
                        color={app.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Chip
                        label={app.type === 'test' ? 'Teszt' : 'Demó'}
                        color={app.type === 'test' ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {app.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<PlayIcon />}
                      onClick={() => handleOpenApp(app.url)}
                      disabled={app.status !== 'active'}
                      sx={{ mr: 1 }}
                    >
                      Indítás
                    </Button>
                    {canManageApp && (
                      <>
                        {app.status === 'inactive' ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PlayIcon />}
                            onClick={() => handleStartApp(app.id)}
                          >
                            Aktiválás
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<StopIcon />}
                            onClick={() => handleStopApp(app.id)}
                          >
                            Leállítás
                          </Button>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          size="small"
                          component={Link}
                          to={`/apps/${app.id}`}
                          startIcon={<InfoIcon />}
                        >
                          Részletek
                        </Button>
                      </>
                    )}
                  </CardActions>
                </Card>
              ))
            )}
          </Box>
        </Box>

        {/* Admin kezelőfelületek, csak admin felhasználók láthatják */}
        {user?.role === 'admin' && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Adminisztráció
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: {
                xs: '1fr',                     // Mobil: egy oszlop
                sm: 'repeat(2, 1fr)',          // Tablet: két oszlop
                md: 'repeat(4, 1fr)'           // Asztali: négy oszlop
              }, 
              gap: 3 
            }}>
              <Card
                component={Link}
                to="/apps/manage"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  textDecoration: 'none',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Alkalmazások kezelése
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Alkalmazások hozzáadása, szerkesztése, törlése
                  </Typography>
                </CardContent>
              </Card>
              
              <Card
                component={Link}
                to="/users"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  textDecoration: 'none',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Felhasználók kezelése
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Felhasználók hozzáadása, szerkesztése, törlése
                  </Typography>
                </CardContent>
              </Card>
              
              <Card
                component={Link}
                to="/system"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  textDecoration: 'none',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Rendszer állapot
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rendszer erőforrások, szolgáltatások monitorozása
                  </Typography>
                </CardContent>
              </Card>
              
              <Card
                component={Link}
                to="/settings"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  textDecoration: 'none',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: `0 12px 20px -10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                }}
              >
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Beállítások
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rendszer beállítások, konfigurációk kezelése
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default Dashboard;